import Color from "@arcgis/core/Color";
import Map from "@arcgis/core/Map";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import MapView from "@arcgis/core/views/MapView";
import Search from "@arcgis/core/widgets/Search";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import LayerListViewModel from "@arcgis/core/widgets/LayerList/LayerListViewModel";
import LayerList from "@arcgis/core/widgets/LayerList";
import DistanceMeasurement2D from "@arcgis/core/widgets/DistanceMeasurement2D";
import LayerSearchSource from "@arcgis/core/widgets/Search/LayerSearchSource";
import { GISType } from "./gis-types";
import { SimpleFillSymbol } from "@arcgis/core/symbols";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Graphic from "@arcgis/core/Graphic";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import Geometry from "@arcgis/core/geometry/Geometry";
import Polyline from "@arcgis/core/geometry/Polyline";
import Polygon from "@arcgis/core/geometry/Polygon";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import {
  cut,
  geodesicBuffer,
  intersects,
  rotate,
  union,
} from "@arcgis/core/geometry/geometryEngine";
import Point from "@arcgis/core/geometry/Point";
import "@esri/calcite-components/dist/components/calcite-button";
import "@esri/calcite-components/dist/components/calcite-modal";
import "@esri/calcite-components/dist/components/calcite-tabs";
import "@esri/calcite-components/dist/components/calcite-tab";
import "@esri/calcite-components/dist/components/calcite-tab-nav";
import "@esri/calcite-components/dist/components/calcite-tab-title";
import "@esri/calcite-components/dist/components/calcite-input-number";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import PortalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/PortalBasemapsSource";
import Portal from "@arcgis/core/portal/Portal";
import Basemap from "@arcgis/core/Basemap";
import Expand from "@arcgis/core/widgets/Expand";
import TileInfo from "@arcgis/core/layers/support/TileInfo";
import Slider from "@arcgis/core/widgets/Slider";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import FillSymbol from "@arcgis/core/symbols/FillSymbol";
import MarkerSymbol from "@arcgis/core/symbols/MarkerSymbol";
import Collection from "@arcgis/core/core/Collection";
import { GISSearchResult } from "./gis-search-result";
import { GISLayerSearchResult } from "./gis-layer-search-result";
import FieldInfo from "@arcgis/core/popup/FieldInfo";
import CustomContent from "@arcgis/core/popup/content/CustomContent";
import { setAssetPath } from "@esri/calcite-components/dist/components";
import { CalciteInputNumber } from "@esri/calcite-components/dist/components/calcite-input-number";
import { CalciteList } from "@esri/calcite-components/dist/components/calcite-list";
import { CalciteListItem } from "@esri/calcite-components/dist/components/calcite-list-item";
import { CalciteAction } from "@esri/calcite-components/dist/components/calcite-action";
import CoordinateConversion from "@arcgis/core/widgets/CoordinateConversion";
import { UtmCoordinatesSearchSource } from "./utm-coordinates-search-source";
import Fullscreen from "@arcgis/core/widgets/Fullscreen";
import SketchTooltipOptions from "@arcgis/core/views/interactive/sketch/SketchTooltipOptions";
import {
  createBodyFromGraphics,
  deleteShapes,
  encodeFormData,
  isGraphicHit,
  isPolygon,
  setShapeOnBeforeUnloadEventHandler,
  unsetShapeOnBeforeUnloadEventHandler,
  updateBodyFromGraphic,
  validateGraphicPolygon,
  validatePolygon,
} from "./shape-util";
import { project } from "@arcgis/core/geometry/projection";
import { MapUI } from "./map-ui";
import { GisEditContext, GisEditParameters } from "./gis-edit-context";
import { IdentityManagerRegisterTokenProps } from "./geospatial-util";
import { kml } from "@tmcw/togeojson";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import shp from "shpjs";
import JSZip from "jszip";
import {
  Geometry as GeoJsonGeometry,
  Feature,
  GeoJsonProperties,
  Polygon as GeoJsonPolygon,
  MultiPolygon as GeoJsonMultiPolygon,
  Position,
} from "geojson";
import SearchSearchCompleteEvent = __esri.SearchSearchCompleteEvent;

// CDN hosted assets
setAssetPath("https://js.arcgis.com/calcite-components/2.8.0/assets");

// eslint-disable-next-line no-unused-vars
export function createMap(
  parent: DotNet.DotNetObject | null,
  params: GisEditParameters,
) {
  try {
    const ctx = new GisEditContext(params);

    const mapUI = new MapUI(ctx);

    const spinnerOrNull = document.getElementById("gis-spinner");
    let spinner: HTMLElement;
    if (spinnerOrNull != null) {
      spinner = spinnerOrNull;
    } else {
      throw new Error("Loading Spinner not available");
    }

    const editSymbolColor = new Color("#c894ff");
    editSymbolColor.a = 0.3;
    const editSymbol = new SimpleFillSymbol({
      color: editSymbolColor,
      style: "solid",
      outline: {
        color: new Color("#78716e"),
        width: 1,
      },
    });

    const shapeHoverSymbolColor = new Color("#51f7fd");
    shapeHoverSymbolColor.a = 0.5;
    const shapeHoverSymbol = new SimpleFillSymbol({
      color: shapeHoverSymbolColor,
      style: "solid",
      outline: {
        color: new Color("#3b3736"),
        width: 1,
      },
    });

    const shapeSelectedSymbolColor = new Color("#6fff83");
    shapeSelectedSymbolColor.a = 0.5;
    const shapeSelectedSymbol = new SimpleFillSymbol({
      color: shapeSelectedSymbolColor,
      style: "solid",
      outline: {
        color: new Color("#51f7fd"),
        width: 2.25,
      },
    });

    const ringCalloutSymbolColor = new Color("#3b3736");
    const ringCalloutSymbol = new SimpleFillSymbol({
      color: ringCalloutSymbolColor,
      style: "diagonal-cross",
      outline: {
        color: new Color("#51f7fd"),
        width: 2.25,
      },
    });

    const markerSymbol = new SimpleMarkerSymbol({
      color: new Color("#c894ff"),
    });
    const markerHoverSymbol = new SimpleMarkerSymbol({
      color: new Color("#51f7fd"),
    });
    const markerSelectedSymbol = new SimpleMarkerSymbol({
      color: new Color("#6fff83"),
    });
    const markerCalloutSymbol = new SimpleMarkerSymbol({
      color: new Color("#00faad"),
    });

    const baseLayer = new MapImageLayer({
      title: "Base Layer",
      url: ctx.params.baseLayerURL,
      sublayers: [
        {
          id: 1,
          title: "Interstates",
        },
        {
          id: 2,
          title: "Highways",
        },
        {
          id: 5,
          title: "Counties",
          opacity: 0.5,
        },
      ],
    });

    const quadLayer = new FeatureLayer({
      title: "Quads",
      url: `${ctx.params.baseLayerURL}/4`,
      popupTemplate: new PopupTemplate({
        title: "Quad Name",
        content: [
          {
            type: "fields",
            fieldInfos: [
              {
                fieldName: "QuadName",
                label: "Quad Name",
              },
              {
                fieldName: "Status",
                label: "Status",
              },
              {
                fieldName: "Zone",
                label: "Zone",
              },
            ],
          },
        ],
      }),
      popupEnabled: false,
      minScale: 250000,
    });

    const PLSSLayer = new FeatureLayer({
      title: "PLSS",
      url: `${ctx.params.baseLayerURL}/3`,
      popupTemplate: new PopupTemplate({
        title: "PLSS",
        content: "{Data}",
      }),
      popupEnabled: false,
      minScale: 250000,
    });

    const geoNameLayer = new FeatureLayer({
      title: "Geographic Names",
      url: `${ctx.params.baseLayerURL}/0`,
      popupTemplate: new PopupTemplate({
        title: "Geographic Name",
        content: [
          {
            type: "fields",
            fieldInfos: [
              {
                label: "Name",
                fieldName: "NAME",
              },
              {
                label: "Type",
                fieldName: "TYPE",
              },
              {
                label: "County",
                fieldName: "COUNTY",
              },
              {
                label: "Topoquad",
                fieldName: "TOPOQUAD",
              },
            ],
          },
        ],
      }),
      popupEnabled: false,
      minScale: 250000,
      visible: false,
    });

    const createLabelClass = (expression: string) => {
      return new LabelClass({
        symbol: new TextSymbol({
          color: "black",
          haloColor: "white",
          haloSize: 1,
          font: {
            family: "Avenir Next LT Pro",
            style: "normal",
            weight: "normal",
          },
        }),
        labelExpressionInfo: {
          expression,
        },
        labelPlacement: "always-horizontal",
      });
    };

    // NMCRIS feature layers
    const bGISAcceptedField = new FieldInfo({
      label: "GIS Accepted",
      fieldName: "bGISAccepted",
    });

    const activityLayer = new FeatureLayer({
      title: "Archaeological Surveys",
      url: ctx.params.activityLayerUrl,
      minScale: 250000,
      outFields: ["*"],
      popupTemplate: new PopupTemplate({
        title: "Archaeological Survey",
        content: [
          new CustomContent({
            outFields: ["*"],
            creator: (event): HTMLElement => {
              const span = document.createElement("span");
              if (!event) return span;
              const graphic = event.graphic;
              const iActivityNum = graphic.attributes["iActivityNum"];
              const link = document.createElement("a");
              link.target = "_blank";
              link.innerText = `NMCRIS ${iActivityNum}`;
              link.href = `Activities/${iActivityNum}`;
              span.appendChild(link);
              return span;
            },
          }),
          {
            type: "fields",
            fieldInfos: [
              {
                label: "Lead Agency",
                fieldName: "sLeadAgency",
              },
              {
                label: "Performing Organization",
                fieldName: "sPerfOrgName",
              },
              bGISAcceptedField,
            ],
          },
        ],
      }),
      labelingInfo: [createLabelClass("$feature.iActivityNum")],
    });
    const hcpiLayer = new FeatureLayer({
      title: "HCPI Resources",
      url: ctx.params.hcpiLayerURL,
      minScale: 250000,
      outFields: ["*"],
      popupTemplate: new PopupTemplate({
        title: "HCPI Resource",
        content: [
          new CustomContent({
            outFields: ["*"],
            creator: (event): HTMLElement => {
              const span = document.createElement("span");
              if (!event) return span;
              const graphic = event.graphic;
              const iNMSeqNum = graphic.attributes["iNMSeqNum"];
              const ActivityNum = graphic.attributes["ActivityNum"] ?? "";
              const link = document.createElement("a");
              link.target = "_blank";
              link.innerText = `HCPI ${iNMSeqNum}`;
              link.href = `Resources?form_prefix=2&resourceNumbers=${iNMSeqNum}&activityNumbers=${ActivityNum}`;
              span.appendChild(link);
              return span;
            },
          }),
          {
            type: "fields",
            fieldInfos: [
              {
                label: "NMCRIS #",
                fieldName: "ActivityNum",
              },
              {
                label: "Resource Type",
                fieldName: "SMPrefix",
              },
              {
                label: "Aliases",
                fieldName: "sAlias",
              },
              bGISAcceptedField,
            ],
          },
        ],
      }),
      labelingInfo: [createLabelClass("$feature.iNMSeqNum")],
    });
    const districtLayer = new FeatureLayer({
      title: "Historic Districts",
      url: ctx.params.districtLayerURL,
      minScale: 250000,
      outFields: ["*"],
      popupTemplate: new PopupTemplate({
        title: "Historic District",
        content: [
          {
            type: "fields",
            fieldInfos: [
              {
                label: "HCPI Number",
                fieldName: "iNMSeqNum",
              },
              {
                label: "Aliases",
                fieldName: "sAlias",
              },
              bGISAcceptedField,
            ],
          },
        ],
      }),
      labelingInfo: [createLabelClass("$feature.iNMSeqNum")],
    });
    const siteLayer = new FeatureLayer({
      title: "Archaeological Sites",
      url: ctx.params.siteLayerURL,
      minScale: 250000,
      outFields: ["*"],
      popupTemplate: new PopupTemplate({
        title: "Archaeological Site",
        content: [
          new CustomContent({
            outFields: ["*"],
            creator: (event): HTMLElement => {
              const span = document.createElement("span");
              if (!event) return span;
              const graphic = event.graphic;
              const iResourceID = graphic.attributes["iResourceID"];
              const iNMSeqNum = graphic.attributes["iNMSeqNum"];
              const ActivityNum = graphic.attributes["ActivityNum"] ?? "";
              const link = document.createElement("a");
              link.target = "_blank";
              link.innerText = `LA ${iNMSeqNum}`;
              link.href = `Resources/ResourceStatus/${iResourceID}?iActivityNum=${ActivityNum}`;
              span.appendChild(link);
              return span;
            },
          }),
          {
            type: "fields",
            fieldInfos: [
              {
                label: "NMCRIS #",
                fieldName: "ActivityNum",
              },
              bGISAcceptedField,
            ],
          },
        ],
      }),
      labelingInfo: [createLabelClass("$feature.iNMSeqNum")],
    });

    const registerLayer = new FeatureLayer({
      title: "State Register Properties",
      url: ctx.params.registerLayerURL,
      minScale: 250000,
      outFields: ["iNMSeqNum"],
      popupTemplate: new PopupTemplate({
        title: "State Register Property",
        content: [
          new CustomContent({
            outFields: ["*"],
            creator: (event): HTMLElement => {
              const span = document.createElement("span");
              if (!event) return span;
              const graphic = event.graphic;
              const iResourceID = graphic.attributes["iResourceID"];
              const SR_Number = graphic.attributes["SR_Number"];
              const link = document.createElement("a");
              link.target = "_blank";
              link.innerText = `SR ${SR_Number}`;
              link.href = `RegisterProperty/${iResourceID}`;
              span.appendChild(link);
              return span;
            },
          }),
          {
            type: "fields",
            fieldInfos: [bGISAcceptedField],
          },
        ],
      }),
      labelingInfo: [createLabelClass("$feature.iNMSeqNum")],
    });

    const covenantLayer = new FeatureLayer({
      title: "Covenants",
      url: ctx.params.covenantLayerURL,
      minScale: 250000,
      outFields: ["*"],
      popupTemplate: new PopupTemplate({
        title: "Covenant",
        content: [
          new CustomContent({
            outFields: ["*"],
            creator: (event): HTMLElement => {
              const span = document.createElement("span");
              if (!event) return span;
              const graphic = event.graphic;
              const iCovenantID = graphic.attributes["iCovenantID"];
              const link = document.createElement("a");
              link.target = "_blank";
              link.innerText = `COV ${iCovenantID}`;
              link.href = `Covenants/${iCovenantID}`;
              span.appendChild(link);
              return span;
            },
          }),
          {
            type: "fields",
            fieldInfos: [
              {
                label: "Property Name",
                fieldName: "sPropertyName",
              },
              {
                label: "Address",
                fieldName: "sAddress",
              },
              {
                label: "County",
                fieldName: "sCounty",
              },
              {
                fieldName: "expression/start",
              },
              {
                fieldName: "expression/end",
              },
            ],
          },
        ],
        expressionInfos: [
          {
            name: "start",
            title: "Start Date",
            expression: 'Text(ToUTC($feature.dStartDate), "MM/DD/Y")',
          },
          {
            name: "end",
            title: "End Date",
            expression: 'Text(ToUTC($feature.dEndDate), "MM/DD/Y")',
          },
        ],
      }),
      labelingInfo: [createLabelClass("$feature.iCovenantID")],
    });

    // Currently the editLayer isn't used for anything except querying
    // features and then displaying them in the graphicLayer to allow
    // for editing
    let nonEditLayer: FeatureLayer, editLayer: FeatureLayer;
    if (ctx.gisType === GISType.Activity) {
      nonEditLayer = activityLayer.clone();
      nonEditLayer.definitionExpression = `iActivityNum <> ${ctx.params.id}`;
      editLayer = new FeatureLayer({
        url: ctx.params.featureServerLayerURL,
        minScale: 250000,
        definitionExpression: `coalesce(ArmsActivityID, ActivityNumber) = ${ctx.params.id}`,
      });
    } else if (ctx.gisType === GISType.Covenant) {
      nonEditLayer = covenantLayer.clone();
      nonEditLayer.definitionExpression = `iCovenantID <> ${ctx.params.id}`;
      editLayer = new FeatureLayer({
        url: ctx.params.featureServerLayerURL,
        minScale: 250000,
        definitionExpression: `iCovenantID = ${ctx.params.id}`,
      });
    } else {
      if (ctx.gisType === GISType.Site) {
        nonEditLayer = siteLayer.clone();
      } else if (ctx.gisType === GISType.StateRegister) {
        nonEditLayer = registerLayer.clone();
      } else if (ctx.gisType === GISType.Building) {
        nonEditLayer = hcpiLayer.clone();
      } else if (ctx.gisType === GISType.Structure) {
        nonEditLayer = hcpiLayer.clone();
      } else if (ctx.gisType === GISType.LinearResource) {
        nonEditLayer = hcpiLayer.clone();
      } else if (ctx.gisType === GISType.Object) {
        nonEditLayer = hcpiLayer.clone();
      } else if (ctx.gisType === GISType.District) {
        nonEditLayer = districtLayer.clone();
      } else {
        throw new Error("The provided GIS type was not recognized.");
      }
      nonEditLayer.definitionExpression = `iResourceID <> ${ctx.params.id}`;
      editLayer = new FeatureLayer({
        url: ctx.params.featureServerLayerURL,
        minScale: 250000,
        outFields: ["iResourceID", "ActivityNum"],
        // Show resources for this activity number and the parent shapes.
        definitionExpression: `iResourceID = ${ctx.params.id}`,
      });
    }
    const baseTitle = nonEditLayer.title;
    nonEditLayer.title = `Non-Editable ${baseTitle}`;

    const graphicLayer = new GraphicsLayer({
      graphics: [],
      minScale: 250000,
      title: `Editable ${baseTitle}`,
    });

    const ringCalloutLayer = new GraphicsLayer({
      graphics: [],
      listMode: "hide",
    });
    const pointLayer = new GraphicsLayer({
      graphics: [],
      listMode: "hide",
      maxScale: 270000,
    });

    const customSearchResultsLayer = new GraphicsLayer({
      graphics: [],
      listMode: "hide",
    });

    const group = new GroupLayer({
      title: "Layers",
      layers: [
        baseLayer,
        pointLayer,
        customSearchResultsLayer,
        quadLayer,
        PLSSLayer,
        geoNameLayer,
        nonEditLayer,
        graphicLayer,
        ringCalloutLayer,
      ].concat(
        (
          [
            [[GISType.Covenant], covenantLayer],
            [[GISType.StateRegister], registerLayer],
            [
              [
                GISType.Object,
                GISType.LinearResource,
                GISType.Structure,
                GISType.Building,
              ],
              hcpiLayer,
            ],
            [[GISType.Activity], activityLayer],
            [[GISType.Site], siteLayer],
          ] as [[GISType], FeatureLayer][]
        )
          .filter(([t, _]) => !t.includes(ctx.gisType))
          .map(([_, l]) => {
            l.visible = false;
            return l;
          }),
      ),
    });

    // basemaps
    // Setting up this "default" portal basemaps source will include all the "default" basemaps associated with our API key.
    const source = new PortalBasemapsSource({
      portal: new Portal({
        url: ctx.params.portalServer,
      }),
    });
    // Create our custom topographic basemap.
    const customTopo = new Basemap({
      baseLayers: [
        new MapImageLayer({
          url: "https://services.arcgisonline.com/ArcGIS/rest/services/USA_Topo_Maps/MapServer", // USA Topo Maps, drawn last until zoomed in past 1 in:0.2 mi scale.
        }),
      ],
      title: "USA Topo",
      thumbnailUrl:
        "https://www.arcgis.com/sharing/rest/content/items/99cd5fbd98934028802b4f797c4b1732/info/thumbnail/USA_Topo_Thumbnails.jpg",
    });
    // After fetching the "default" basemaps, prepend the collection of items with our custom topographic basemap.
    source.updateBasemapsCallback = (basemaps: Basemap[]) => {
      basemaps.unshift(customTopo);
      return basemaps;
    };

    // map
    const map = new Map({
      basemap: customTopo, // World Basemap v2, World Contours, & USA Topo
      layers: [group],
    });

    const view = new MapView({
      container: "map",
      map,
      center: [-106, 34],
      zoom: 7,
      constraints: {
        lods: TileInfo.create({
          numLODs: 25, // zoom to level of detail of scale 1 in:2 ft
        }).lods,
      },
    });

    // fullscreen widget
    const gisTab = document.getElementById("gis-tab");
    if (gisTab)
      view.ui.add(
        new Fullscreen({
          element: gisTab,
        }),
        {
          position: "top-left",
          index: 0, // before zoom buttons
        },
      );

    // base map gallery - under expand
    const basemapGalleryButtonDiv = document.createElement("div");
    basemapGalleryButtonDiv.className = "esri-shadow";
    new Expand({
      view,
      content: new BasemapGallery({
        view: view,
        source: source,
      }),
      expandIconClass: "esri-icon-basemap",
      expandTooltip: "Basemap Gallery",
      autoCollapse: true,
      closeOnEsc: true,
      container: basemapGalleryButtonDiv,
    });
    view.ui.add(basemapGalleryButtonDiv, "top-right");

    const searchDiv =
      document.getElementById("gis-edit-search") ??
      document.createElement("div"); // this shouldn't happen.
    view.when((graphic: __esri.Graphic) => {
      const searchWidget = new Search({
        view,
        container: searchDiv,
        includeDefaultSources: false,
        allPlaceholder: "Activity/LA/HCPI/SR", // Allow searching all layers from the sources below
        searchAllEnabled: true,
        locationEnabled: false,
        minSuggestCharacters: 1,
        sources: [
          {
            placeholder: "Address",
            suggest: true,
            name: "Addresses",
            singleLineFieldName: "SingleLine",
            categories: ["Address"],
            url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer",
            maxSuggestions: 3,
            minSuggestCharacters: 8,
          },
          new UtmCoordinatesSearchSource(
            customSearchResultsLayer,
            view,
          ).searchSource(),
          {
            layer:
              ctx.gisType === GISType.Activity ? nonEditLayer : activityLayer,
            name: activityLayer.title,
            searchFields: ["iActivityNum"],
            displayField: "iActivityNum",
            exactMatch: true,
            placeholder: "Activity Number",
            maxSuggestions: 1,
          },
          {
            layer: ctx.gisType === GISType.Site ? nonEditLayer : siteLayer,
            name: siteLayer.title,
            searchFields: ["iNMSeqNum"],
            displayField: "iNMSeqNum",
            exactMatch: true,
            placeholder: "LA Num",
            maxSuggestions: 1,
          },
          {
            layer:
              ctx.gisType === GISType.Covenant ? nonEditLayer : covenantLayer,
            name: covenantLayer.title,
            searchFields: ["iCovenantID"],
            displayField: "iCovenantID",
            exactMatch: true,
            placeholder: "COV Num",
            maxSuggestions: 1,
          },
          {
            layer: quadLayer,
            name: quadLayer.title,
            searchFields: ["QuadName"],
            displayField: "QuadName",
            exactMatch: false,
            placeholder: "Quad Name",
          },
          {
            layer: PLSSLayer,
            name: PLSSLayer.title,
            searchFields: ["plss", "Township", "Range", "SectionNum"],
            displayField: "plss",
            exactMatch: false,
            placeholder: "32N-13W-7",
            outFields: ["plss"], // need to specify this as an outfield. otherwise, the query returns empty-string as "SearchResult.name". the "first" defined string is the only default.
          },
          {
            layer: geoNameLayer,
            name: geoNameLayer.title,
            searchFields: ["NAME", "COUNTY", "TOPOQUAD"],
            displayField: "NAME",
            exactMatch: false,
            placeholder: "Name/County/Topoquad",
          },
          {
            layer: [
              GISType.Object,
              GISType.LinearResource,
              GISType.Structure,
              GISType.Building,
            ].includes(ctx.gisType)
              ? nonEditLayer
              : hcpiLayer,
            name: hcpiLayer.title,
            searchFields: ["iNMSeqNum"],
            displayField: "iNMSeqNum",
            exactMatch: true,
            placeholder: "HCPI No.",
            maxSuggestions: 1,
          },
          {
            layer:
              ctx.gisType === GISType.StateRegister
                ? nonEditLayer
                : registerLayer,
            name: registerLayer.title,
            searchFields: ["iNMSeqNum"],
            displayField: "iNMSeqNum",
            exactMatch: true,
            placeholder: "State Reg. No.",
            maxSuggestions: 1,
          },
        ] as Partial<LayerSearchSource>[],
      });
      const sketchViewModel = new SketchViewModel({
        view,
        layer: graphicLayer,
        updateOnGraphicClick: false,
        polygonSymbol: editSymbol,
        tooltipOptions: new SketchTooltipOptions({
          enabled: true,
        }),
      });

      const gisToolBox = document.getElementById("gisToolBox");

      view.ui.add(mapUI.editButtonsGroup, "top-right");

      /**
       * Controls the loading spinner and read-only behavior on all edit buttons.
       *
       * If the user is read-only, then any call to setCount removes the loading spinner and disables all edit
       * buttons. This case occurs after the map and shapes load for a read-only user to remove the loading
       * spinner and disable the edit buttons.
       *
       *
       * @param c If the user is read-only, remove the loading spinner and disable all edit buttons.
       *  - If `c` is null, show the loading spinner and disable all edit buttons. Call setCount(null) when "loading."
       *  - If `c` is nonnull, remove the loading spinner and enable all edit buttons. Call setCount(nonnull) when finished "loading."
       */
      const setCount = (c: number | null) => {
        if (ctx.readonly) {
          spinner.style.display = "none";
          mapUI.disableAll();
        } else if (c == null) {
          spinner.style.removeProperty("display");
          mapUI.disableAll();
        } else {
          spinner.style.display = "none";
          mapUI.enableAll();
        }
      };
      setCount(null);

      const showError = (message: string) => {
        return showCriticalError(message, false);
      };
      const showCriticalError = (message: string, requireReload = true) => {
        document.getElementById("error-modal")?.setAttribute("open", "");
        const content = document.getElementById("error-modal-content");
        const reloadButton = document.getElementById(
          "error-modal-reload-button",
        );
        if (content != null) content.innerText = message;
        if (requireReload) {
          ctx.readonly = true;
          setCount(null);
          if (reloadButton) reloadButton.style.display = "block";
        } else {
          if (reloadButton) reloadButton.style.display = "none";
          setCount(1);
        }
      };

      let featuresSelected: number[] = [];
      const colorShapeAs =
        (graphicSymbol: FillSymbol, pointSymbol: MarkerSymbol) =>
        (objectId: number) => {
          graphicLayer.graphics // color the selected shape
            .filter((hg) => hg.attributes.OBJECTID == objectId)
            .forEach((hg: Graphic) => {
              hg.symbol = graphicSymbol;
            });
          pointLayer.graphics // color the selected point
            .filter((p) => p.attributes.OBJECTID == objectId)
            .forEach((hg: Graphic) => {
              hg.symbol = pointSymbol;
            });
        };
      const colorShapeAsSelected = colorShapeAs(
        shapeSelectedSymbol,
        markerSelectedSymbol,
      );
      const colorShapeAsHovered = colorShapeAs(
        shapeHoverSymbol,
        markerHoverSymbol,
      );
      const colorShapeAsDefault = colorShapeAs(editSymbol, markerSymbol);

      const confirmModal =
        document.getElementById("confirm-modal") ??
        document.createElement("div");
      const confirmModalTitle =
        document.getElementById("confirm-modal-title") ??
        document.createElement("div");
      const confirmModalContent =
        document.getElementById("confirm-modal-content") ??
        document.createElement("div");
      const confirmModalButton =
        document.getElementById("confirm-modal-button") ??
        document.createElement("div");

      const userHasPermissionForShape = (graphic: Graphic): boolean =>
        ctx.params.userIsHPDORAdmin || // admins can edit any shape
        // non-admin users can edit any shape in the edit layer
        (!ctx.readonly && // as long as they're the Performing Agency user and the Activity is "Data entry in progress (201)"
          (ctx.params.activityNum == null || // ctx.params.activityNum represents the Activity number of the Resource Visit we're editing. If this is null, we're editing a NIAF shape.
            graphic.attributes.ActivityNum == ctx.params.activityNum) && // If it is a resource shape, only let users edit shapes under this activity (not parent shapes where ActivityNum is null)
          !graphic.attributes.bGISAccepted); // as long as they're not Approved

      // use the featuresSelected array as the source of truth for checkboxes and manually update all checkboxes.
      const updateShapesListCheckboxes = () => {
        let allObjectIds = graphicLayer.graphics.map(
          (g) => g.attributes.OBJECTID,
        );
        allObjectIds.forEach((objectId) => {
          let checkbox = document.getElementById(
            `object-${objectId}`,
          ) as HTMLInputElement;
          checkbox.checked = featuresSelected.includes(objectId);
          if (featuresSelected.includes(objectId)) {
            // shape is selected, set to "selected" color.
            colorShapeAsSelected(objectId);
          } else {
            colorShapeAsDefault(objectId);
          }
        });
      };
      const shapeListToggleChecked = (objectId: number) => {
        const selectedFeatureIndex = featuresSelected.indexOf(objectId);
        if (selectedFeatureIndex == -1) {
          // feature shape is not already selected, select it..
          featuresSelected.push(objectId);
        } else if (selectedFeatureIndex > -1) {
          // the checkbox isn't checked, attempt to unselect the feature shape.
          featuresSelected.splice(selectedFeatureIndex, 1); // remove 1 checked element...
        }
      };

      function refreshNodes(
        graphics: Graphic[],
        dotnetParent: DotNet.DotNetObject | null,
      ) {
        const nodes = graphics.map((g) => {
          const shapeContainer = document.createElement("div");
          shapeContainer.className =
            "list-group-item user-select-none pl-1 pr-0 pb-2";
          const label = document.createElement("label");
          label.htmlFor = `object-${g.attributes.OBJECTID}`;
          label.className =
            "d-flex justify-content-between align-items-center mb-1";
          // this container is so the focus outline only surrounds the checkbox and isn't stretched vertically
          const checkboxContainer = document.createElement("div");
          checkboxContainer.className = "d-flex align-items-center";
          const checkbox = document.createElement("input");
          checkbox.className = "mx-2";
          checkbox.type = "checkbox";
          checkbox.tabIndex = 3;
          checkbox.id = `object-${g.attributes.OBJECTID}`;
          checkbox.onclick = (e) => {
            if (e.shiftKey) {
              if (featuresSelected.length == 0) {
                // Nothing else selected, just select the shape shift-clicked.
                shapeListToggleChecked(g.attributes.OBJECTID);
              }
              const allObjectIds = graphics.map((g) => g.attributes.OBJECTID);
              const indexLastSelected = allObjectIds.indexOf(
                featuresSelected[featuresSelected.length - 1],
              );
              const indexJustSelected = allObjectIds.indexOf(
                g.attributes.OBJECTID,
              );
              if (indexJustSelected < indexLastSelected) {
                for (
                  let n = indexLastSelected - 1;
                  n >= indexJustSelected;
                  n--
                ) {
                  shapeListToggleChecked(allObjectIds[n]);
                }
              } else {
                for (
                  let n = indexLastSelected + 1;
                  n <= indexJustSelected;
                  n++
                ) {
                  shapeListToggleChecked(allObjectIds[n]);
                }
              }
            } else {
              shapeListToggleChecked(g.attributes.OBJECTID);
            }
            updateShapesListCheckboxes();
          };
          if (!userHasPermissionForShape(g)) {
            // prevent merge/delete by disabling checkbox for shapes that the user doesn't have perms on.
            checkbox.setAttribute("disabled", "");
          }
          checkboxContainer.append(checkbox);
          const labelsDiv = document.createElement("div");
          labelsDiv.className = "d-flex flex-column flex-grow-1";
          labelsDiv.style.margin = "auto 0";
          const span = document.createElement("span");
          span.innerText = `SHAPE-${g.attributes.OBJECTID.toString(16).toUpperCase()}`;
          // span.innerText = `SHAPE-${g.attributes.OBJECTID}`; // DEBUGGING PURPOSES ONLY. DON'T COMMIT THIS UNCOMMENTED.
          labelsDiv.append(span);

          if (g.attributes.ActivityNum) {
            const actNumLabel = document.createElement("div");
            actNumLabel.className = "small";
            actNumLabel.innerText = `NMCRIS # ${g.attributes.ActivityNum}`;
            labelsDiv.append(actNumLabel);
          }

          if ("bGISAccepted" in g.attributes) {
            const accepted = document.createElement("div");
            accepted.className = "small";
            accepted.innerText = g.attributes.bGISAccepted
              ? "Approved"
              : "Proposed";
            labelsDiv.append(accepted);
          }

          const navigateButton = document.createElement("div");
          navigateButton.className =
            "esri-icon-navigation esri-widget--button esri-widget esri-interactive esri-shadow mr-2";
          navigateButton.title = "Zoom to shape";
          navigateButton.tabIndex = 0;
          navigateButton.setAttribute("role", "button");
          navigateButton.onclick = (e) => {
            e.preventDefault();
            view.goTo(g);
          };
          label.append(navigateButton, labelsDiv);

          const rings = (g.geometry as Polygon).rings;
          if (rings.length > 1) {
            const partsButton = document.createElement("div");
            partsButton.className =
              "esri-widget--button esri-widget esri-interactive border-right";
            const listButtonIcon = document.createElement("calcite-icon");
            listButtonIcon.setAttribute("icon", "list");
            listButtonIcon.setAttribute("scale", "s");
            partsButton.appendChild(listButtonIcon);
            partsButton.title = `View parts`;
            partsButton.tabIndex = 1;
            partsButton.setAttribute("role", "button");
            let partsList: CalciteList | null = null;
            partsButton.onclick = (e) => {
              e.preventDefault();
              if (partsList) {
                partsList.remove();
                partsList = null;
                return;
              }
              partsList = new CalciteList();

              const splitAll = document.createElement("div");
              splitAll.innerHTML = `<span>Comprises <strong>${rings.length}</strong> parts</span>`;
              splitAll.className =
                "d-flex align-items-center justify-content-between";
              const splitAllAction = new CalciteAction();
              splitAllAction.icon = "unmerge";
              splitAllAction.scale = "s";
              splitAllAction.text = "Separate all";
              splitAllAction.title = "Separate all parts";
              splitAllAction.setAttribute("text-enabled", "");
              splitAllAction.onclick = (e) => {
                e.preventDefault();
                confirmModalTitle.innerText = `Separate Parts`;
                confirmModalContent.innerHTML = `Separate SHAPE-${g.attributes.OBJECTID.toString(16).toUpperCase()} into its <strong>${rings.length}</strong> parts?`;
                confirmModalButton.setAttribute("color", "blue");
                confirmModalButton.innerText = "Separate all";
                confirmModalButton.onclick = async (_) => {
                  confirmModal.removeAttribute("open");
                  partsList?.remove();
                  partsList = null;
                  ringCalloutLayer.removeAll();

                  // Create new graphics from rings array
                  const createShapesJobs: Promise<any>[] = rings
                    .map((ring) => {
                      return new Graphic({
                        geometry: new Polygon({
                          rings: [ring],
                          spatialReference: g.geometry.spatialReference,
                        }),
                      });
                    })
                    .map((graphic) => {
                      return createShape(graphic);
                    });
                  await executeShapeOperation(
                    Promise.all(createShapesJobs).then(
                      async () =>
                        await deleteShapes(
                          [g.attributes.OBJECTID],
                          ctx.params.deleteURL,
                          await tokenForUrl(ctx.params.deleteURL),
                          "Failed to delete original shape.",
                        ),
                    ),
                    "SA1",
                    "add separated shape",
                  );
                };
                confirmModal.setAttribute("open", "");
              };
              splitAll.appendChild(splitAllAction);
              partsList.appendChild(splitAll);

              rings.forEach((r, i) => {
                const ringPoly = new Polygon();
                ringPoly.rings = [r];
                ringPoly.spatialReference = g.geometry.spatialReference;
                const ringGraphic = new Graphic({
                  geometry: ringPoly,
                  symbol: ringCalloutSymbol,
                });

                const li = new CalciteListItem();
                li.label = `Part ${i + 1}`;
                const zt = new CalciteAction();
                zt.icon = "compass";
                zt.slot = "actions-end";
                zt.scale = "s";
                zt.title = `Zoom to part ${i + 1}`;
                zt.text = `Zoom to`;
                zt.setAttribute("text-enabled", "");
                zt.onclick = (e) => {
                  e.preventDefault();
                  view.goTo({ center: ringPoly });
                };
                li.appendChild(zt);
                if (userHasPermissionForShape(g)) {
                  const ct = new CalciteAction();
                  ct.icon = "add-in";
                  ct.slot = "actions-end";
                  ct.scale = "s";
                  ct.title = "Separate part";
                  ct.text = "Separate";
                  ct.setAttribute("text-enabled", "");
                  ct.onclick = (e) => {
                    confirmModalTitle.innerText = `Separate Part`;
                    confirmModalContent.innerHTML = `Separate part from SHAPE-${g.attributes.OBJECTID.toString(16).toUpperCase()} into its own shape?`;
                    confirmModalButton.setAttribute("color", "blue");
                    confirmModalButton.innerText = "Separate";
                    confirmModalButton.onclick = async (_) => {
                      confirmModal.removeAttribute("open");
                      partsList?.remove();
                      partsList = null;
                      ringCalloutLayer.removeAll();

                      // Replace existing shape with all rings except this one
                      const remainingRings = (
                        g.geometry as Polygon
                      ).rings.slice();
                      remainingRings.splice(i, 1);
                      const createShapesJobs: Promise<any>[] = [
                        createShape(
                          new Graphic({
                            geometry: new Polygon({
                              rings: remainingRings,
                              spatialReference: g.geometry.spatialReference,
                            }),
                          }),
                        ),
                      ];
                      // Create new graphic from ring
                      createShapesJobs.push(
                        createShape(
                          new Graphic({
                            geometry: new Polygon({
                              rings: [r],
                              spatialReference: g.geometry.spatialReference,
                            }),
                          }),
                        ),
                      );

                      await executeShapeOperation(
                        Promise.all(createShapesJobs).then(
                          async () =>
                            await deleteShapes(
                              [g.attributes.OBJECTID],
                              ctx.params.deleteURL,
                              await tokenForUrl(ctx.params.deleteURL),
                              "Failed to delete original shape.",
                            ),
                        ),
                        "SP1",
                        "add split shape",
                      );
                    };
                    confirmModal.setAttribute("open", "");
                  };
                  li.appendChild(ct);
                }

                li.onmouseover = () => {
                  ringCalloutLayer.add(ringGraphic);
                };
                li.onmouseout = () => {
                  ringCalloutLayer.remove(ringGraphic);
                };
                partsList?.appendChild(li);
              });
              shapeContainer.appendChild(partsList);
            };
            label.append(partsButton);
          }

          if (!ctx.readonly) {
            const duplicateButton = document.createElement("div");
            duplicateButton.className =
              "esri-icon-duplicate esri-widget--button esri-widget esri-interactive border-right";
            duplicateButton.title = "Create a duplicate copy of this shape";
            duplicateButton.tabIndex = 1;
            duplicateButton.setAttribute("role", "button");
            duplicateButton.onclick = (e) => {
              e.preventDefault();
              confirmModalTitle.innerText = `Duplicate Shape`;
              confirmModalContent.innerText = `Create a new, duplicate shape of SHAPE-${g.attributes.OBJECTID.toString(16).toUpperCase()}?`;
              confirmModalButton.setAttribute("color", "blue");
              confirmModalButton.innerText = "Duplicate";
              confirmModalButton.onclick = async (_) => {
                confirmModal.removeAttribute("open");
                const dupeShape = g;
                g.attributes.OBJECTID = null;
                await executeShapeOperation(
                  createShape(dupeShape),
                  "DU1",
                  "create duplicate shape",
                );
              };
              confirmModal.setAttribute("open", "");
            };
            label.append(duplicateButton);

            label.append(checkboxContainer);
          }

          const hoverGraphic = () => {
            colorShapeAsHovered(g.attributes.OBJECTID);
          };
          const unHoverGraphic = () => {
            if (featuresSelected.indexOf(g.attributes.OBJECTID) > -1) {
              // shape is selected, set back to "selected" color on mouse-out.
              colorShapeAsSelected(g.attributes.OBJECTID);
              return;
            }
            colorShapeAsDefault(g.attributes.OBJECTID);
          };
          label.onmouseover = hoverGraphic;
          label.onmouseout = unHoverGraphic;
          shapeContainer.appendChild(label);
          return shapeContainer;
        });
        const parentShapeList = document.getElementById("shape-list");
        parentShapeList?.replaceChildren(...nodes);
        const points = graphics
          // Check to see if we are working with a polygon (true in most (all?) cases)
          .filter((g) => isPolygon(g.geometry))
          // Extract the 'rings' (shapes that were merged together into one polygon)
          .flatMap((g) => (g.geometry as Polygon).rings.map((r) => [g, r]))
          // Create a new Polygon, and return the centroid for each of the extracted 'rings'
          .map(([g, r]) => {
            let ring = r as number[][];
            let poly = new Polygon();
            poly.addRing(ring);
            poly.spatialReference = (g as Graphic).geometry.spatialReference;
            return [g, poly.centroid];
          })
          // Create a new point for each of the centroids
          .map(
            ([g, p]) =>
              new Graphic({
                geometry: p as Point,
                symbol: markerSymbol,
                attributes: {
                  OBJECTID: (g as Graphic).attributes.OBJECTID,
                  bGISAccepted: (g as Graphic).attributes.bGISAccepted,
                },
              }),
          );
        // Add the created points to the point layer
        pointLayer.removeAll();
        pointLayer.addMany(points);

        // Set the "Select All" button behavior
        mapUI.selectAllButton.onclick = () => {
          featuresSelected = graphics
            .filter((g) => userHasPermissionForShape(g))
            .map((g) => {
              colorShapeAsSelected(g.attributes.OBJECTID);
              return g.attributes.OBJECTID;
            });
          updateShapesListCheckboxes();
        };
      }

      const query = editLayer.createQuery();
      query.maxRecordCountFactor = 1.0; // multiplied by the maxRecordCount on the layer
      editLayer
        .queryFeatures(query)
        .then((featureSet) => {
          graphicLayer.removeAll();
          const sortedFeatures = featureSet.features
            .map((f) => {
              f.symbol = editSymbol;
              return f;
            })
            .sort((a, b) => a.attributes.OBJECTID - b.attributes.OBJECTID);
          graphicLayer.addMany(sortedFeatures);
          refreshNodes(sortedFeatures, parent);
          setCount(sortedFeatures.length);
          if (sortedFeatures.length == 1) {
            view.goTo(sortedFeatures[0]);
          } else if (sortedFeatures.length > 1) {
            view.goTo(union(sortedFeatures.map((g) => g.geometry)));
          }
        })
        .catch((err) => {
          showCriticalError(
            `Failed to load this ${ctx.gisType.humanReadable}'s Shapes. Please reload the page and try again. Error Code Q1`,
          );
          console.error(err);
        });

      const selectShapes = (objectIds: number[] | Collection<number>) => {
        objectIds
          .filter((objectId) => featuresSelected.indexOf(objectId) == -1)
          .forEach((objectId) => {
            // ensure we're not selecting a shape the user doesn't have permission for
            if (
              !userHasPermissionForShape(
                graphicLayer.graphics
                  ?.filter((g) => g?.attributes?.OBJECTID == objectId)
                  .at(0),
              )
            )
              return;
            // if not already checked, select it, and color it as selected.
            // feature shape is not already selected, select it, updates the shapes list checkboxes, and color the shape as selected.
            featuresSelected.push(objectId);
            colorShapeAsSelected(objectId);
          });
        updateShapesListCheckboxes();
      };

      async function refreshEditLayer(editedObjectId?: number): Promise<any> {
        // pass in the "editedObjectId" if you're refreshing an existing shape.
        featuresSelected = []; // clear the existing shape selection set
        const query = editLayer.createQuery();
        query.maxRecordCountFactor = 1.0;
        try {
          let featureSet = await editLayer.queryFeatures(query);
          // capture existing set of objectIds so we can find and select the newly added shape, if applicable.
          const oldShapesObjectIds =
            graphicLayer.graphics?.map(
              (g) => g?.attributes?.OBJECTID as number,
            ) ?? [];

          const sortedFeatures = featureSet.features
            .map((f) => {
              f.symbol = editSymbol;
              return f;
            })
            .sort((a, b) => a.attributes.OBJECTID - b.attributes.OBJECTID);
          graphicLayer.removeAll();
          graphicLayer.addMany(sortedFeatures);

          // find any new shapes
          const newShapes = Array.from(
            graphicLayer.graphics?.filter(
              (newShape) =>
                !oldShapesObjectIds?.includes(
                  newShape?.attributes?.OBJECTID as number,
                ), // filter only new shapes
            ),
          );

          refreshNodes(sortedFeatures, parent); // rebuild the shapes list
          setCount(sortedFeatures.length);

          if (editedObjectId) {
            // we're editing an existing shape, select it when done.
            selectShapes([editedObjectId]);
            const editedObject = sortedFeatures.find(
              (g) => g.attributes.OBJECTID == editedObjectId,
            );
            if (editedObject) await view.goTo(editedObject);
          } else if (newShapes) {
            selectShapes(
              newShapes.map((g) => g?.attributes?.OBJECTID as number),
            );
            await view.goTo(newShapes);
          }
        } catch (err) {
          showCriticalError(
            `Failed to refresh this ${ctx.gisType.humanReadable}'s shapes. Please reload the page and try again. Error Code Q2`,
          );
          console.error(err);
        }
      }

      // merge button
      mapUI.mergeButton.onclick = () => {
        if (featuresSelected.length < 2) {
          // nothing to merge.
          // Scold the user.
          confirmModalTitle.innerText = `Not enough shapes selected`;
          confirmModalContent.innerText = `Select at least two shapes to merge.`;
          confirmModalButton.setAttribute("color", "blue");
          confirmModalButton.innerText = `Ok`;
          confirmModalButton.onclick = (_) => {
            confirmModal.removeAttribute("open");
          };
          confirmModal.setAttribute("open", "");
          return;
        }
        const mergeGraphics = graphicLayer.graphics.filter((g) =>
          featuresSelected.includes(g.attributes.OBJECTID),
        );
        // If we're mergine resource shapes that could have an ActivityNum, determine if we're merging with a "parent"
        // shape (that doesn't have an activity number). If so, don't preserve the activity number on the resulting
        // merged shape.
        const parentResourceShapesCount: number = mergeGraphics.filter(
          (g) => g.attributes.ActivityNum == null,
        ).length;
        if (ctx.params.activityNum && parentResourceShapesCount > 0) {
          confirmModalContent.innerHTML = `Merge ${featuresSelected.length - parentResourceShapesCount} Activity Shape(s)\
          and ${parentResourceShapesCount} Parent Shape(s) &rarrtl; into 1 Parent Shape?`;
        } else {
          confirmModalContent.innerText = `Merge ${featuresSelected.length} Shapes?`;
        }

        confirmModalTitle.innerText = `Merge Shapes`;
        confirmModalButton.setAttribute("color", "blue");
        confirmModalButton.innerText = `Merge ${featuresSelected.length} Shapes`;
        confirmModalButton.onclick = async (_) => {
          setCount(null); // start loading spinner
          sketchViewModel.cancel();
          confirmModal.removeAttribute("open");
          // collect shapes affected by merge selection
          const mergeGraphics = graphicLayer.graphics.filter((g) =>
            featuresSelected.includes(g.attributes.OBJECTID),
          );
          const mergeGeometries = mergeGraphics.map((gr) => gr.geometry);
          const mergedGeometry = union(Array.from(mergeGeometries));
          // upload mergedGeometry
          await executeShapeOperation(
            createShape(new Graphic({ geometry: mergedGeometry })).then(
              async () => await deleteShapesInChunks(featuresSelected),
            ),
            "M1",
            "add merged shapes",
          );
        };
        confirmModal.setAttribute("open", "");
      };

      // delete
      const deleteShapesInChunks = async (features: number[]): Promise<any> => {
        let chunkedDeleteShapesPromises = [];
        const chunkSize = 100;
        for (let i = 0; i < features.length; i += chunkSize) {
          const chunk = features.slice(i, i + chunkSize);
          chunkedDeleteShapesPromises.push(
            deleteShapes(
              chunk,
              ctx.params.deleteURL,
              await tokenForUrl(ctx.params.deleteURL),
              "Failed to delete shape(s).",
            ),
          );
        }
        return Promise.all(chunkedDeleteShapesPromises);
      };
      mapUI.deleteButton.onclick = () => {
        if (featuresSelected.length == 0) {
          // nothing to delete.
          // Scold the user
          confirmModalTitle.innerText = `No shapes selected`;
          confirmModalContent.innerText = `Select at least one shape to delete.`;
          confirmModalButton.setAttribute("color", "blue");
          confirmModalButton.innerText = `Ok`;
          confirmModalButton.onclick = (_) => {
            confirmModal.removeAttribute("open");
          };
          confirmModal.setAttribute("open", "");
          return;
        }
        confirmModal.setAttribute("open", "");
        confirmModalTitle.innerText = `Delete Shape(s)`;
        confirmModalContent.innerText = `Are you sure you want to delete ${featuresSelected.length} Shape(s)?`;
        confirmModalButton.setAttribute("color", "red");
        confirmModalButton.innerText = `Delete ${featuresSelected.length} Shape(s)`;
        confirmModalButton.onclick = async (_) => {
          confirmModal.removeAttribute("open");
          sketchViewModel.cancel();

          const chunkedRequestsPromise = deleteShapesInChunks(featuresSelected);
          await executeShapeOperation(
            chunkedRequestsPromise,
            "D1",
            "delete shape(s)",
          );
        };
      };

      // "replace" esri "sidebar" above shapes list with custom shapes buttons

      // ruler
      let measurementWidgetDiv: HTMLElement | null;
      let measurementWidget: DistanceMeasurement2D;
      const rulerButton = document.createElement("div");
      rulerButton.className =
        "esri-icon-measure-line esri-widget--button esri-widget esri-interactive esri-shadow";
      rulerButton.title = "Measure distance";
      rulerButton.tabIndex = 0;
      rulerButton.setAttribute("role", "button");
      rulerButton.onclick = (e) => {
        e.preventDefault();
        // If the measurement widget is already instantiated then we're closing the ruler tool.
        if (measurementWidgetDiv) {
          gisToolBox?.removeChild(measurementWidgetDiv);
          measurementWidget.destroy();
          measurementWidgetDiv = null;
          return;
        }

        measurementWidgetDiv = document.createElement("div");
        measurementWidgetDiv.className = "esri-shadow";
        measurementWidget = new DistanceMeasurement2D({
          view: view,
          container: measurementWidgetDiv,
          unitOptions: [
            "metric",
            "kilometers",
            "meters",
            "imperial",
            "miles",
            "yards",
            "feet",
            "inches",
          ],
        });
        measurementWidget.unit = "metric";
        measurementWidget.viewModel.start();

        gisToolBox?.prepend(measurementWidgetDiv);
      };
      view.ui.add(rulerButton, {
        position: "top-right",
      });

      // coordinate conversion widget
      let coordsWidgetDiv: HTMLElement | null;
      let coordsWidget: CoordinateConversion;
      const coordsButton = document.createElement("div");
      coordsButton.className =
        "esri-icon-map-pin esri-widget--button esri-widget esri-interactive esri-shadow";
      coordsButton.title = "Convert coordinates";
      coordsButton.tabIndex = 0;
      coordsButton.setAttribute("role", "button");
      coordsButton.onclick = (e) => {
        e.preventDefault();
        // If the coords widget is already instantiated then we're closing the coords tool.
        if (coordsWidgetDiv) {
          gisToolBox?.removeChild(coordsWidgetDiv);
          coordsWidget.destroy();
          coordsWidgetDiv = null;
          return;
        }

        coordsWidgetDiv = document.createElement("div");
        coordsWidgetDiv.className = "esri-shadow";
        coordsWidget = new CoordinateConversion({
          view: view,
          container: coordsWidgetDiv,
        });

        gisToolBox?.prepend(coordsWidgetDiv);
      };
      view.ui.add(coordsButton, {
        position: "top-right",
      });

      const createShape = async (...graphic: Graphic[]): Promise<any> => {
        const body = createBodyFromGraphics(
          graphic,
          ctx.gisType,
          await tokenForUrl(ctx.params.createURL),
          ctx.params.id,
          ctx.params.activityNum,
        );
        return await fetch(ctx.params.createURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: encodeFormData(body),
        })
          .then((res) => res.json())
          .then((res) => {
            if ("addResults" in res) {
              const errors: string[] = res["addResults"]
                .filter((r: any) => "error" in r)
                .map((r: any) => r.error.description);
              if (errors.length > 0) {
                throw new Error(
                  "Failed to add drawn shape. " + errors.join(", "),
                );
              }
            }
          });
      };

      /**
       * This is a wrapper for a given shape modification operation that:
       * - handles errors
       * - calls AfterShape
       * - refreshes the edit layer (and shapes list)
       * @param shapeOperation Shape modification operation
       * @param errorCode Unique code to display when the operation fails
       * @param operationDescription
       * @param objectId OBJECTID of the shape on which `shapeOperation` is operating, if applicable.
       */
      const executeShapeOperation = async (
        shapeOperation: Promise<any>,
        errorCode: string,
        operationDescription: string,
        objectId?: number,
      ) => {
        setCount(null); // show the loading spinner, usually to be reset by a subsequent call to .then(async r => await refreshEditLayer());
        let shapeOperationResult;
        try {
          shapeOperationResult = await wrapInMarkNotAccepted(
            parent,
            shapeOperation,
          );
        } catch (err) {
          showCriticalError(
            `Failed to ${operationDescription ?? "perform operation"}. Please reload the page and try again. Error code ${errorCode}. ${err}`,
          );
          console.log(operationDescription, err);
          throw err;
        }
        await afterShape(parent);
        await refreshEditLayer(objectId);
        return shapeOperationResult;
      };

      const exitAllTools = async () => {
        sketchViewModel.cancel(); // draw tool has no tool window
        await hotDogExit();
        rectangleToolExit();
        circleToolExit();
        await unsetShapeOnBeforeUnloadEventHandler(parent);
      };

      const editToolOpen = () => {
        return (
          sketchViewModel.state == "active" ||
          hotDogToolDiv ||
          rectangleToolDiv ||
          circleToolDiv ||
          cutToolDiv
        );
      };

      // draw
      mapUI.drawButton.addEventListener("click", async (_event) => {
        if (
          sketchViewModel.state == "active" &&
          sketchViewModel.activeTool == "polygon"
        ) {
          // already drawing. stop drawing.
          sketchViewModel.cancel();
          return;
        }
        await exitAllTools();
        sketchViewModel.create("polygon");
        await setShapeOnBeforeUnloadEventHandler(parent);
      });
      sketchViewModel.on("create", async (event) => {
        if (event.state == "cancel") {
          // user aborted
          await exitAllTools();
        }
        if (event.state != "complete") {
          return;
        }

        await unsetShapeOnBeforeUnloadEventHandler(parent);
        let newGraphic = event.graphic;
        if (event.tool == "rectangle") {
          if (
            rectangleToolWidth &&
            rectangleToolWidth.value &&
            rectangleToolLength &&
            rectangleToolLength.value
          ) {
            // User defined width and length
            const width = Number.parseFloat(rectangleToolWidth.value),
              length = Number.parseFloat(rectangleToolLength.value);
            if (width && length) {
              let newPolygon = event.graphic.geometry as Polygon;
              // Find the center point of the rectangle
              const wmPoint = new Point({
                x: newPolygon.extent.center.x,
                y: newPolygon.extent.center.y,
                spatialReference: event.graphic.geometry.spatialReference,
              });
              // Project the center point of the rectangle from Web Mercator to NAD83 / UTM zone 13N
              const projPoint = project(
                wmPoint,
                new SpatialReference({ wkid: 26913 }),
              ) as Point;

              // Generate the coordinates of the four corners of the new rectangle
              const modProjPointTL = new Point({
                x:
                  projPoint.x -
                  width / 2 / projPoint.spatialReference.metersPerUnit,
                y:
                  projPoint.y +
                  length / 2 / projPoint.spatialReference.metersPerUnit,
                spatialReference: projPoint.spatialReference,
              });
              const modProjPointTR = new Point({
                x:
                  projPoint.x +
                  width / 2 / projPoint.spatialReference.metersPerUnit,
                y:
                  projPoint.y +
                  length / 2 / projPoint.spatialReference.metersPerUnit,
                spatialReference: projPoint.spatialReference,
              });
              const modProjPointBR = new Point({
                x:
                  projPoint.x +
                  width / 2 / projPoint.spatialReference.metersPerUnit,
                y:
                  projPoint.y -
                  length / 2 / projPoint.spatialReference.metersPerUnit,
                spatialReference: projPoint.spatialReference,
              });
              const modProjPointBL = new Point({
                x:
                  projPoint.x -
                  width / 2 / projPoint.spatialReference.metersPerUnit,
                y:
                  projPoint.y -
                  length / 2 / projPoint.spatialReference.metersPerUnit,
                spatialReference: projPoint.spatialReference,
              });

              // Set the rings array using the new coordinates
              newPolygon.rings = [
                [
                  [modProjPointTL.x, modProjPointTL.y],
                  [modProjPointTR.x, modProjPointTR.y],
                  [modProjPointBR.x, modProjPointBR.y],
                  [modProjPointBL.x, modProjPointBL.y],
                ],
              ];
              newPolygon.spatialReference = projPoint.spatialReference;

              // Cheat. Manually rotate the new rectangle to align with the cardinal directions.
              // This slope/intercept was generated by observing how the NAD83 / UTM zone 13N wkid projection
              // skews the angle of a newly generated rectangle using the strategy above. For example, in the
              // leftmost corners of the state, rectangles will point up and to the left, so rotate them clockwise.
              // Opposite for the rightmost corners of the state.
              const rotateCheatAngle =
                0.000007148305907 * projPoint.x + -3.509273600675918;
              newPolygon = rotate(newPolygon, rotateCheatAngle) as Polygon;
              newGraphic.geometry = newPolygon;
            }
          }
          rectangleToolExit();
        }
        if (event.tool == "circle") {
          if (circleToolDiameter && circleToolDiameter.value) {
            // User defined radius
            const diameter = Number.parseFloat(circleToolDiameter.value);
            if (diameter) {
              let newPolygon = event.graphic.geometry as Polygon;
              // Find the center point of the circle
              const wmPoint = new Point({
                x: newPolygon.extent.center.x,
                y: newPolygon.extent.center.y,
                spatialReference: event.graphic.geometry.spatialReference,
              });
              newPolygon = geodesicBuffer(
                wmPoint,
                diameter / 2,
                "meters",
              ) as Polygon;
              newGraphic.geometry = newPolygon;
            }
          }
          circleToolExit();
        }
        await executeShapeOperation(
          createShape(newGraphic),
          "C1",
          "add new shape",
        );
      });

      sketchViewModel.on("update", async (event) => {
        if (event.state != "complete") return;

        if (event.aborted) {
          await refreshEditLayer();
        }
        if (!event.aborted) {
          const updateBody = updateBodyFromGraphic(
            event.graphics[0],
            ctx.gisType,
            await tokenForUrl(ctx.params.editURL),
            ctx.params.id,
          );
          await executeShapeOperation(
            fetch(ctx.params.editURL, {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: encodeFormData(updateBody),
            }).then(async (response) => {
              if (!response.ok) {
                let result = await response.json();
                throw new Error(
                  `Response not OK. ${"error" in result ? result.error : null}`,
                );
              }
            }),
            "US1",
            "update shape",
            event.graphics[0].attributes.OBJECTID,
          );
        }
        await updateOptionsExit();
      });

      if (!ctx.readonly) {
        view.on("click", (event) => {
          if (measurementWidget?.viewModel?.state == "measuring") {
            // don't edit shapes while measuring
            return;
          }
          view
            .hitTest(event, {
              include: [graphicLayer],
            })
            .then(async (response) => {
              const hitGraphic = response.results
                .map((g) => (isGraphicHit(g) ? g?.graphic : null))
                .find((g) => g != null);
              if (
                hitGraphic != null &&
                userHasPermissionForShape(hitGraphic) &&
                !editToolOpen()
              ) {
                await sketchViewModel.update([hitGraphic], { tool: "reshape" });
                // show save/copy dialog
                await updateOptionsCreate();
              }
            });
        });
      }

      // update options div
      let updateOptionsDiv: HTMLElement | null;
      const updateOptionsExit = async () => {
        if (updateOptionsDiv) {
          gisToolBox?.removeChild(updateOptionsDiv);
          updateOptionsDiv = null;
          await unsetShapeOnBeforeUnloadEventHandler(parent);
        }
      };
      const updateOptionsCreate = async () => {
        await setShapeOnBeforeUnloadEventHandler(parent);
        let updateOptionsRevert: HTMLElement;
        let updateOptionsSubmit: HTMLElement;
        let updateOptionsUndo: HTMLElement;
        let updateOptionsRedo: HTMLElement;

        // create the update options div "widget"
        updateOptionsDiv = document.createElement("div");
        updateOptionsDiv.className =
          "esri-widget esri-widget--panel esri-shadow";
        updateOptionsDiv.style.padding = "10px";

        const updateOptionsTitle = document.createElement("h6");
        updateOptionsTitle.innerText = "Edit Shape";
        updateOptionsDiv.appendChild(updateOptionsTitle);

        updateOptionsRevert = document.createElement("div");
        updateOptionsRevert.ariaRoleDescription = "button";
        updateOptionsRevert.className =
          "esri-button esri-button--tertiary mb-2";
        updateOptionsRevert.title =
          "Revert changes to selected shape and stop editing";
        updateOptionsRevert.innerText = "Revert shape";
        updateOptionsRevert.onclick = (e) => {
          e.preventDefault();
          sketchViewModel.cancel();
          updateOptionsExit();
        };

        let undoRedoDiv: HTMLElement;
        undoRedoDiv = document.createElement("div");
        undoRedoDiv.className = "d-flex mb-2";

        updateOptionsUndo = document.createElement("div");
        updateOptionsUndo.ariaRoleDescription = "button";
        updateOptionsUndo.className = "esri-button esri-button";
        updateOptionsUndo.title = "Undo most recent change to selected shape";
        updateOptionsUndo.innerText = "Undo";
        updateOptionsUndo.onclick = (e) => {
          e.preventDefault();
          sketchViewModel.undo();
        };

        updateOptionsRedo = document.createElement("div");
        updateOptionsRedo.ariaRoleDescription = "button";
        updateOptionsRedo.className = "esri-button esri-button--secondary";
        updateOptionsRedo.title = "Redo most recent change to selected shape";
        updateOptionsRedo.innerText = "Redo";
        updateOptionsRedo.onclick = (e) => {
          e.preventDefault();
          sketchViewModel.redo();
        };

        undoRedoDiv.appendChild(updateOptionsUndo);
        undoRedoDiv.appendChild(updateOptionsRedo);

        updateOptionsSubmit = document.createElement("div");
        updateOptionsSubmit.ariaRoleDescription = "button";
        updateOptionsSubmit.className = "esri-button esri-button--primary";
        updateOptionsSubmit.title =
          "Save changes to selected shape and stop editing";
        updateOptionsSubmit.innerText = "Save changes";
        updateOptionsSubmit.onclick = (e) => {
          e.preventDefault();
          sketchViewModel.complete();
        };

        updateOptionsDiv.appendChild(updateOptionsRevert);
        updateOptionsDiv.appendChild(undoRedoDiv);
        updateOptionsDiv.appendChild(updateOptionsSubmit);
        gisToolBox?.prepend(updateOptionsDiv);
      };

      // hot dog tool - Line Buffer
      let hotDogToolDiv: HTMLElement | null;
      let hotDogBufferSizeSlider: Slider;
      let hotDogLineSketch: Geometry | null;
      let hotDogLineSketchLayer: GraphicsLayer;
      let hotDogBufferSketchLayer: GraphicsLayer;
      let hotDogSketchViewModel: SketchViewModel;
      let hotDogBufferSizeMeters = 15;
      let hotDogSubmit: HTMLElement;
      const updateHotDogBuffer = () => {
        if (!hotDogLineSketch || hotDogBufferSizeMeters <= 0) {
          // insufficient data to create a hot dog
          hotDogBufferSketchLayer.removeAll();
          // no hot dog sketch yet. disable the submit button.
          hotDogSubmit.setAttribute("disabled", "");
          return;
        }
        const hotDogResult = geodesicBuffer(
          hotDogLineSketch,
          hotDogBufferSizeMeters,
          "meters",
        );
        let hotDog: Polygon;
        if (hotDogResult instanceof Array) {
          hotDog = hotDogResult[0];
        } else {
          hotDog = hotDogResult;
        }
        if (hotDogBufferSketchLayer.graphics.length === 0) {
          hotDogBufferSketchLayer.add(
            new Graphic({
              geometry: hotDog,
              symbol: hotDogSketchViewModel.polygonSymbol,
            }),
          );
          hotDogSubmit.removeAttribute("disabled");
        } else {
          hotDogBufferSketchLayer.graphics.getItemAt(0).geometry = hotDog;
          // updating existing hot dog. no need to remove the 'disabled' attribute from the submit button.
        }
      };

      const hotDogReset = () => {
        // clean up
        if (hotDogLineSketch) {
          hotDogLineSketch.destroy();
          hotDogLineSketch = null;
        }
        hotDogLineSketchLayer.removeAll();
        hotDogBufferSketchLayer.removeAll();
      };
      const hotDogExit = async () => {
        if (hotDogToolDiv) {
          hotDogReset();
          gisToolBox?.removeChild(hotDogToolDiv);
          hotDogToolDiv = null;
          view.map.removeMany([hotDogBufferSketchLayer, hotDogLineSketchLayer]);
          hotDogBufferSketchLayer.destroy();
          hotDogLineSketchLayer.destroy();
          hotDogSketchViewModel.destroy();
          hotDogBufferSizeSlider.destroy();
          await unsetShapeOnBeforeUnloadEventHandler(parent);
        }
      };
      mapUI.hotDogToolButton.addEventListener("click", async (_event) => {
        if (hotDogToolDiv) {
          await hotDogExit();
          return;
        }
        await exitAllTools();
        await setShapeOnBeforeUnloadEventHandler(parent);
        // create the hot dog tool "widget"
        hotDogToolDiv = document.createElement("div");
        hotDogToolDiv.className = "esri-widget esri-widget--panel esri-shadow";
        hotDogToolDiv.style.padding = "10px";

        const hotDogToolTitle = document.createElement("h6");
        hotDogToolTitle.innerText = "Buffered-line Tool";
        hotDogToolDiv.appendChild(hotDogToolTitle);
        const hotDogToolInstructions = document.createElement("p");
        hotDogToolInstructions.innerText =
          "Draw a line on the map and set a buffer radius by dragging the slider or " +
          "clicking the radius and entering a custom value.";
        hotDogToolDiv.appendChild(hotDogToolInstructions);

        const hotDogSliderDiv = document.createElement("div");
        hotDogSliderDiv.className = "mt-4";
        hotDogBufferSizeSlider = new Slider({
          container: hotDogSliderDiv,
          min: 0,
          max: Math.max(hotDogBufferSizeMeters, 100),
          labelInputsEnabled: true, // allow user to set hot dog buffer radius
          rangeLabelInputsEnabled: true, // allow user to set min/max range of slider
          steps: 1,
          visibleElements: {
            labels: true,
            rangeLabels: true,
          },
          precision: 2,
          labelFormatFunction: (value, type) => {
            return `${value.toString()} m`;
          },
          values: [hotDogBufferSizeMeters],
        });
        const hotDogBufferSizeChanged = (event: { value: number }) => {
          hotDogBufferSizeMeters = event.value;
          updateHotDogBuffer();
        };
        hotDogBufferSizeSlider.on("thumb-change", hotDogBufferSizeChanged);
        hotDogBufferSizeSlider.on("thumb-drag", hotDogBufferSizeChanged);
        hotDogToolDiv.appendChild(hotDogSliderDiv);

        hotDogSubmit = document.createElement("div");
        hotDogSubmit.ariaRoleDescription = "button";
        hotDogSubmit.className = "esri-button esri-button--primary mt-2 w-auto";
        hotDogSubmit.title = "Submit shape";
        hotDogSubmit.innerText = "Submit shape";
        hotDogSubmit.setAttribute("disabled", "");
        hotDogSubmit.onclick = async (e) => {
          e.preventDefault();
          let hotDogGraphic = hotDogBufferSketchLayer?.graphics?.getItemAt(0);
          if (!hotDogGraphic) {
            // No hot dog was drawn. This shouldn't happen, the button should be
            // disabled if there's no hot dog to submit.
            return;
          }
          hotDogSketchViewModel.complete(); // stop editing hot dogs
          hotDogSketchViewModel.updateOnGraphicClick = false; // prevent the user from editing hot dogs while submitting
          hotDogSubmit.setAttribute("disabled", ""); // prevent submitting the same hot dog twice
          hotDogBufferSizeSlider.disabled = true; // prevent hot dog buffer size slider from sliding
          await executeShapeOperation(
            createShape(hotDogGraphic),
            "HD1",
            "add buffered-line shape",
          ).then(async () => {
            await hotDogExit();
          });
        };
        hotDogToolDiv.appendChild(hotDogSubmit);

        gisToolBox?.prepend(hotDogToolDiv);

        hotDogLineSketchLayer = new GraphicsLayer({ listMode: "hide" }); // don't show this graphics layer in the layer list
        hotDogBufferSketchLayer = new GraphicsLayer({ listMode: "hide" });
        view.map.addMany([hotDogBufferSketchLayer, hotDogLineSketchLayer]);
        hotDogSketchViewModel = new SketchViewModel({
          layer: hotDogLineSketchLayer,
          view: view,
          polylineSymbol: {
            type: "simple-line",
            color: [0, 168, 255, 0.7],
            width: "7px",
          },
          polygonSymbol: {
            type: "simple-fill",
            color: [0, 168, 255, 0.4],
            outline: {
              color: "black",
              width: "1px",
            },
          },
          tooltipOptions: new SketchTooltipOptions({
            enabled: true,
          }),
        });
        hotDogSketchViewModel.on("create", (event) => {
          // update the filter every time the user finishes drawing the hot dog line geometry
          if (event.state == "complete") {
            hotDogLineSketch = event.graphic.geometry;
            updateHotDogBuffer();
          }
          if (event.state == "cancel") {
            // user hit escape or otherwise cancelled the hot dog drawing. close the hot dog tool div.
            hotDogExit();
          }
        });
        hotDogSketchViewModel.on("update", (event) => {
          // update the filter every time the user updates the hot dog line geometry
          hotDogLineSketch = event.graphics[0].geometry;
          updateHotDogBuffer();
        });
        hotDogSketchViewModel.defaultUpdateOptions = {
          tool: "reshape", // set the default geometry update tool to reshaping instead of transforming by default
        };
        hotDogSketchViewModel.create("polyline"); // start drawing hot dogs
      });

      // cut tool - Polyline
      let cutToolDiv: HTMLElement | null;
      let cutLineSketch: Geometry | null;
      let cutSketchViewModel: SketchViewModel;
      let cutLineSketchLayer: GraphicsLayer;
      let cutSubmit: HTMLElement;

      const cutReset = () => {
        // clean up
        if (cutLineSketch) {
          cutLineSketch.destroy();
          cutLineSketch = null;
        }
        cutLineSketchLayer.removeAll();
      };
      const cutExit = async () => {
        if (cutToolDiv) {
          cutReset();
          gisToolBox?.removeChild(cutToolDiv);
          cutToolDiv = null;
          view.map.remove(cutLineSketchLayer);
          cutLineSketchLayer.destroy();
          cutSketchViewModel.destroy();
          await unsetShapeOnBeforeUnloadEventHandler(parent);
        }
      };
      mapUI.cutToolButton.addEventListener("click", async (_event) => {
        if (cutToolDiv) {
          await cutExit();
          return;
        }
        await exitAllTools();
        await setShapeOnBeforeUnloadEventHandler(parent);
        // create the hot dog tool "widget"
        cutToolDiv = document.createElement("div");
        cutToolDiv.className = "esri-widget esri-widget--panel esri-shadow";
        cutToolDiv.style.padding = "10px";

        const cutToolTitle = document.createElement("h6");
        cutToolTitle.innerText = "Cut Tool";
        cutToolDiv.appendChild(cutToolTitle);
        const cutToolInstructions = document.createElement("p");
        cutToolInstructions.innerText =
          "Draw a line on the map along which to cut (split) existing shapes.";
        cutToolDiv.appendChild(cutToolInstructions);

        cutSubmit = document.createElement("div");
        cutSubmit.ariaRoleDescription = "button";
        cutSubmit.className = "esri-button esri-button--primary mt-2 w-auto";
        cutSubmit.title = "Perform cut";
        cutSubmit.innerText = "Perform cut";
        cutSubmit.setAttribute("disabled", "");
        cutSubmit.onclick = async (e) => {
          e.preventDefault();
          if (cutLineSketch == null) {
            // No cut line was drawn. This shouldn't happen, the button should be
            // disabled if there's no line to cut along.
            return;
          }
          let cutGeom = cutLineSketch;

          const objectIdsToDelete: number[] = [];
          let toCreate = graphicLayer.graphics
            .filter(userHasPermissionForShape)
            .map((g) => {
              // Project the cut line geometry into the spatial reference of the graphics from the
              // graphicLayer.
              const p = project(cutGeom, g.geometry.spatialReference);
              return {
                objectId: g.attributes.OBJECTID,
                geom: g.geometry,
                projectedCutGeom: Array.isArray(p) ? p[0] : p,
              };
            })
            .filter((g) => intersects(g.geom, g.projectedCutGeom))
            .map((g) => {
              const pieces = cut(g.geom, g.projectedCutGeom as Polyline);
              objectIdsToDelete.push(g.objectId);
              return pieces.map((p) => new Graphic({ geometry: p }));
            })
            .toArray()
            .flatMap((y) => y);

          if (toCreate.length == 0) {
            cutToolInstructions.innerHTML = `<span class="badge badge-warning mr-1">Invalid cut</span> This cut line does not bisect one or more existing ${ctx.gisType.humanReadable} shapes for which you have permission to edit.`;
            return;
          }

          await cutExit();

          // Create new graphics from rings array
          const createShapesJobs: Promise<any>[] = toCreate.map((graphic) => {
            return createShape(graphic);
          });
          await executeShapeOperation(
            Promise.all(createShapesJobs).then(
              async () => await deleteShapesInChunks(objectIdsToDelete),
            ),
            "CR1",
            "add cut shapes",
          );
        };
        cutToolDiv.appendChild(cutSubmit);

        gisToolBox?.prepend(cutToolDiv);

        cutLineSketchLayer = new GraphicsLayer({ listMode: "hide" }); // don't show this graphics layer in the layer list
        view.map.add(cutLineSketchLayer);
        cutSketchViewModel = new SketchViewModel({
          layer: cutLineSketchLayer,
          view: view,
          polylineSymbol: {
            type: "simple-line",
            color: [235, 55, 55, 0.7],
            width: "5px",
          },
          tooltipOptions: new SketchTooltipOptions({
            enabled: true,
          }),
        });
        cutSketchViewModel.on("create", (event) => {
          // update the filter every time the user finishes drawing the cut line geometry
          if (event.state == "complete") {
            cutLineSketch = event.graphic.geometry;
            if (cutLineSketch) cutSubmit.removeAttribute("disabled");
          }
          if (event.state == "cancel") {
            // user hit escape or otherwise cancelled the hot dog drawing. close the cut line tool div.
            cutExit();
          }
        });
        cutSketchViewModel.on("update", (event) => {
          // update the filter every time the user updates the hot dog line geometry
          cutLineSketch = event.graphics[0].geometry;
          if (cutLineSketch) cutSubmit.removeAttribute("disabled");
        });
        cutSketchViewModel.defaultUpdateOptions = {
          tool: "reshape", // set the default geometry update tool to reshaping instead of transforming by default
        };
        cutSketchViewModel.create("polyline"); // start drawing cut line
      });

      // rectangle tool
      let rectangleToolDiv: HTMLElement | null;
      let rectangleToolWidth: CalciteInputNumber | null;
      let rectangleToolLength: CalciteInputNumber | null;
      const rectangleToolExit = () => {
        if (rectangleToolDiv) {
          gisToolBox?.removeChild(rectangleToolDiv);
          rectangleToolDiv = null;
          sketchViewModel.cancel();
        }
      };

      // rectangle tool
      mapUI.rectangleToolButton.addEventListener("click", async (_event) => {
        if (rectangleToolDiv) {
          rectangleToolExit();
          return;
        }
        await exitAllTools();
        await setShapeOnBeforeUnloadEventHandler(parent);
        // create the rectangle tool "widget"
        rectangleToolDiv = document.createElement("div");
        rectangleToolDiv.className =
          "esri-widget esri-widget--panel esri-shadow";
        rectangleToolDiv.style.padding = "10px";

        const rectangleToolTitle = document.createElement("h6");
        rectangleToolTitle.innerText = "Rectangle Tool";
        rectangleToolDiv.appendChild(rectangleToolTitle);
        const rectangleToolInstructions = document.createElement("p");
        rectangleToolInstructions.innerText =
          "Draw a rectangle on the map or set Width and Length in meters below to " +
          "create a fixed rectangle.";
        rectangleToolDiv.appendChild(rectangleToolInstructions);

        const widthLengthDiv = document.createElement("div");
        widthLengthDiv.className = "d-flex justify-content-between";

        rectangleToolWidth = new CalciteInputNumber();
        rectangleToolWidth.numberButtonType = "none";
        rectangleToolWidth.placeholder = "Width m.";
        widthLengthDiv.appendChild(rectangleToolWidth);
        rectangleToolLength = new CalciteInputNumber();
        rectangleToolLength.numberButtonType = "none";
        rectangleToolLength.placeholder = "Length m.";
        rectangleToolLength.className = "ml-2";
        widthLengthDiv.appendChild(rectangleToolLength);

        rectangleToolDiv.appendChild(widthLengthDiv);

        gisToolBox?.prepend(rectangleToolDiv);

        // start drawing a rectangle
        sketchViewModel.create("rectangle");
      });

      // circle tool
      let circleToolDiv: HTMLElement | null;
      let circleToolDiameter: CalciteInputNumber | null;
      const circleToolExit = () => {
        if (circleToolDiv) {
          gisToolBox?.removeChild(circleToolDiv);
          circleToolDiv = null;
          sketchViewModel.cancel();
        }
      };

      // circle tool
      mapUI.circleToolButton.addEventListener("click", async (_event) => {
        if (circleToolDiv) {
          circleToolExit();
          return;
        }
        await exitAllTools();
        await setShapeOnBeforeUnloadEventHandler(parent);
        // create the circle tool "widget"
        circleToolDiv = document.createElement("div");
        circleToolDiv.className = "esri-widget esri-widget--panel esri-shadow";
        circleToolDiv.style.padding = "10px";

        const circleToolTitle = document.createElement("h6");
        circleToolTitle.innerText = "Circle Tool";
        circleToolDiv.appendChild(circleToolTitle);
        const circleToolInstructions = document.createElement("p");
        circleToolInstructions.innerText =
          "Draw a circle on the map or set Diameter in meters below to " +
          "create a fixed circle.";
        circleToolDiv.appendChild(circleToolInstructions);

        circleToolDiameter = new CalciteInputNumber();
        circleToolDiameter.numberButtonType = "none";
        circleToolDiameter.placeholder = "Diameter in meters";
        circleToolDiv.appendChild(circleToolDiameter);

        gisToolBox?.prepend(circleToolDiv);

        // start drawing a circle
        sketchViewModel.create("circle");
      });

      // upload
      const hiddenInput = document.createElement("input");
      hiddenInput.type = "file";
      hiddenInput.accept = ".zip,.kml,.kmz";
      hiddenInput.style.display = "none";
      hiddenInput.addEventListener("change", async (_event) => {
        if (hiddenInput.files == null || hiddenInput.files.length === 0) {
          throw new Error("No file uploaded");
        }
        setCount(null);

        let uploadFile = hiddenInput.files[0];
        /**
         * In order to upload kml/kmz/shape(zip) files:
         *  - First, convert them to GeoJSON. We do this so that we can convert them to the "ArcGIS Geometry
         *    Format."
         *    - For kml/kmz, use the "@tmcw/togeojson" library.
         *    - For shapefiles (ZIP), use the shapefile-js library.
         *  - Convert GeoJson to "ArcGIS Geometry Format" using the Terraformer JS lib.
         *  - Build graphics from resulting geometry.
         *  - Call createShape with graphics.
         *
         *  This is the best/fastest way to convert kml/kmz/shape files to the "ArcGIS Geometry Format" locally.
         *  The portal/sharing/rest/content/features/generate endpoint accepts GeoJSON, but that service call
         *  was quite slow, only supports 2 MB shapefiles, and these libraries work great locally.
         */
        let geoJsonFeatures: Feature<
          GeoJsonGeometry | null,
          GeoJsonProperties
        >[];

        if (
          uploadFile.name.endsWith(".kml") ||
          uploadFile.name.endsWith(".kmz")
        ) {
          let kmlFileContent: string;
          if (uploadFile.name.endsWith(".kmz")) {
            // Unzip the kmz to find the kml file within. kmz is just a zip containing a kml.
            let jsZip = new JSZip();
            jsZip = await jsZip.loadAsync(uploadFile);
            let kmlFile: JSZip.JSZipObject;
            const jsZipObjects = jsZip.filter((path, file: JSZip.JSZipObject) =>
              path.endsWith(".kml"),
            );
            if (jsZipObjects.length == 0) {
              showError(
                "KMZ file does not contain a kml file. Please regenerate your KMZ file and try again.",
              );
              return;
            }
            kmlFile = jsZipObjects[0];
            kmlFileContent = await kmlFile.async("string");
          } else {
            kmlFileContent = await uploadFile.text();
          }

          const kmlDOM = new DOMParser().parseFromString(
            kmlFileContent,
            "text/xml",
          );
          // Convert KMLDom to GeoJson
          const geoJson = kml(kmlDOM, {
            skipNullGeometry: true,
          });
          geoJsonFeatures = geoJson.features;
        } else if (uploadFile.name.endsWith(".zip")) {
          const fileArrayBuffer = await uploadFile.arrayBuffer();
          const geojson = await shp(fileArrayBuffer);

          const featureCollections = Array.isArray(geojson)
            ? geojson
            : [geojson];
          geoJsonFeatures = featureCollections.flatMap((fc) => fc.features);
        } else {
          showCriticalError("Unrecognized file upload type. Error Code UF1");
          return;
        }

        const arcGISGeometries = geoJsonFeatures
          .flatMap((f) => f.geometry ?? [])
          .map((f) => {
            if (f.type === "MultiPolygon")
              return f.coordinates.flatMap((c) => c);
            else if (f.type === "Polygon") return f.coordinates;
            else {
              showCriticalError(
                "Unsupported geometry in file upload. Error Code USF1",
              );
              return [];
            }
          })
          .map((coordinates) => {
            let noZgeojson: GeoJsonPolygon = {
              type: "Polygon",
              coordinates: coordinates,
            };
            noZgeojson.coordinates = noZgeojson.coordinates.map((ring) => {
              let r = ring.map((p) => {
                if (p.length == 3) {
                  // This point has a third dimension (z) which we should remove/ignore. This can
                  // happen most of the time when importing from KMLs.
                  return p.slice(0, 2); // take only the first two dimensions (x,y)
                }
                return p;
              });
              return r;
            });

            const geom = geojsonToArcGIS(noZgeojson);
            return geom;
          });

        // "convert" the "Geometry" object as defined by @terraformer/arcgis into the esri Geometry object.
        const polygons = arcGISGeometries
          .filter((agg) => "rings" in agg)
          .map((agg) => new Polygon({ ...agg }))
          .filter(validatePolygon);

        const graphics = polygons.map((geom) => {
          return new Graphic({
            geometry: geom,
          });
        });
        if (graphics.length == 0) {
          showError(
            "No valid shapes found within uploaded file. Ensure that your file contains only polygon geometry using the WGS84 spatial reference. Ensure that shapefiles contain a .prj projection file.",
          );
          return;
        }

        if (graphics.length > 10 && !ctx.params.userIsHPDORAdmin) {
          showError(
            "File contains more than 10 shapes. Please merge/reduce the number of shapes before uploading.",
          );
          return;
        }

        if (graphics.length > 1) {
          setCount(1);
          confirmModalTitle.innerText = `Upload ${graphics.length} shapes`;
          confirmModalContent.innerHTML = `File <code>${uploadFile.name}</code> contains <strong>${graphics.length}</strong> shapes. Upload all shapes?`;
          if (graphics.length > 2_000)
            confirmModalContent.innerHTML += `<br/><strong>NOTE:</strong> The GIS editor only loads up to 2,000 shapes at a time; only some shapes will be displayed.`;
          confirmModalButton.setAttribute("color", "blue");
          confirmModalButton.innerText = `Upload all`;
          confirmModalButton.onclick = async (_) => {
            setCount(null);
            confirmModal.removeAttribute("open");

            const createShapesInChunks = async (
              graphics: Graphic[],
            ): Promise<any> => {
              let chunkedCreateShapesPromises = [];
              const chunkSize = 250;
              for (let i = 0; i < graphics.length; i += chunkSize) {
                const chunk = graphics.slice(i, i + chunkSize);
                chunkedCreateShapesPromises.push(createShape(...chunk));
              }
              return Promise.all(chunkedCreateShapesPromises);
            };

            const chunkedRequestsPromise = createShapesInChunks(graphics);
            await executeShapeOperation(
              chunkedRequestsPromise,
              "U1",
              "upload new shape(s)",
            );
          };
          confirmModal.setAttribute("open", "");
        } else {
          await executeShapeOperation(
            createShape(...graphics),
            "U1",
            "upload new shape(s)",
          );
        }
      });

      mapUI.uploadButton.addEventListener("click", (_) => {
        hiddenInput.click();
      });

      const layerListModel = new LayerListViewModel({
        view,
      });
      const layerList = new LayerList({
        viewModel: layerListModel,
      });
      // Don't do anything if/when a user completes a search.
      searchWidget.goToOverride = () => {};
      searchWidget.autoSelect = false;
      let searchResults: GISLayerSearchResult[] = [];
      const searchComplete = async (
        searchComplete: SearchSearchCompleteEvent,
      ) => {
        if (searchComplete.results.length == 0) {
          return; // no sources (layers) with results, nothing to do.
        }
        for (const layerSourceResult of searchComplete.results.reverse()) {
          if (layerSourceResult.results.length == 0) {
            continue; // no results within this source (layer), nothing to do.
          }

          let layerSearchResults: GISSearchResult[] =
            layerSourceResult.results.map(
              (r) => new GISSearchResult(r.target, r.name),
            );

          let isNMCRISShapeLayer = false;
          let customPopupTitle = false;

          if (layerSourceResult.source.layer) {
            // this was a LayerSearchSource search and not the Addresses LocatorSearchSource.

            // set the visibility for the layers with results equal to true
            layerSourceResult.source.layer.visible = true;

            const layerSearchSource =
              layerSourceResult.source as LayerSearchSource;
            const searchLayer = layerSearchSource.layer as FeatureLayer;

            isNMCRISShapeLayer = ["iActivityNum", "iNMSeqNum"].includes(
              layerSearchSource.displayField,
            );

            // We're interpreting `exactMatch` as an indicator that this layer could have more than 1 shape per result.
            // Need to re-query the layer for all shapes that match.
            if (layerSearchSource.exactMatch) {
              // Query the layer to fetch ALL results. This is necessary since the esri Search Widget only returns the
              // single clicked suggestion result if the user chooses from the suggestions. Hitting enter within the
              // search box works as we want.
              const realSearchQuery = searchLayer.createQuery();
              // recreate the same query that the search performed. e.g.: "iNMSeqNum = 50385"
              realSearchQuery.where = `${layerSearchSource.searchFields[0]} = ${searchComplete.searchTerm}`;
              realSearchQuery.outFields = ["*"];
              realSearchQuery.outSpatialReference = view.spatialReference; // why esri???
              try {
                let featureSet =
                  await searchLayer.queryFeatures(realSearchQuery);
                layerSearchResults = featureSet.features.map(
                  (g) => new GISSearchResult(g),
                );
              } catch (e) {
                console.warn("Failed to query layer for search results", e);
              }
            }
          } else {
            // This wasn't a LayerSearchSource.
            // Only other option is LocatorSearchSource at the moment, so there's no PopupTemplates defined. Use a
            // customPopupTitle.
            customPopupTitle = true;
          }

          // prepend the results to the display array
          searchResults.unshift(
            new GISLayerSearchResult(
              layerSearchResults,
              isNMCRISShapeLayer,
              layerSourceResult.source.name,
              searchComplete.searchTerm,
              customPopupTitle,
            ),
          );
        }
        refreshSearchResults();
      };
      const refreshSearchResults = () => {
        document.getElementById("search-list")?.replaceChildren(
          ...searchResults.map((r) =>
            r.generateSearchResultDiv(view, (e) => {
              // search result remove button handler
              const i = searchResults.indexOf(r);
              if (i > -1) {
                searchResults.splice(i, 1);
                customSearchResultsLayer.removeMany(
                  r.results.map((x) => x.target),
                );
                refreshSearchResults();
              }
            }),
          ),
        );
      };
      searchWidget.on("search-complete", searchComplete);

      view.ui.add(layerList, {
        position: "bottom-right",
      });

      const scaleBar = new ScaleBar({
        view,
      });
      view.ui.add(scaleBar, {
        position: "bottom-left",
      });
    });
  } catch (e) {
    console.error("Failed to start map", e);
  }
}

async function wrapInMarkNotAccepted<T>(
  parent: DotNet.DotNetObject | null,
  then: Promise<T>,
): Promise<T> {
  if (parent != null) {
    let success = await parent.invokeMethodAsync("MarkNotAccepted");
    if (success != null) {
      return then;
    } else {
      throw new Error("Couldn't uncheck GIS Accepted");
    }
  } else {
    return then;
  }
}

async function afterShape(parent: DotNet.DotNetObject | null): Promise<void> {
  if (parent != null) {
    let success = await parent.invokeMethodAsync("AfterShape");
    if (!success) throw new Error(`AfterShape call failed ${success}`);
  } else {
    return Promise.resolve();
  }
}

async function tokenForUrl(serviceUrl: string): Promise<string> {
  return (await IdentityManager.checkSignInStatus(serviceUrl)).token;
}
export async function registerToken(
  tokenProps: IdentityManagerRegisterTokenProps,
) {
  console.debug("Registering token for server", tokenProps);
  IdentityManager.registerToken(tokenProps);
}
